/**
 * @component ry-knob
 * @category  form
 * @summary   Rotary knob input (ry-ui-only — no Fomantic equivalent). A 270° arc maps `[min, max]` to a visual rotation; drag vertically (up = increase), scroll the wheel, or press arrow keys to adjust. Display shows the formatted value; optional [labels] maps integer values to named positions (e.g. "Off, Low, Med, High"). Emits `ry:input` during interaction, `ry:change` on commit.
 *
 * @attribute min          number?   default=0     Minimum value.
 * @attribute max          number?   default=100   Maximum value.
 * @attribute step         number?   default=1     Step increment. Drives keyboard step + snapping. `0` disables snapping.
 * @attribute value        number?   default=0     Current value. Clamped to [min, max].
 * @attribute label        string?              Caption rendered next to the knob.
 * @attribute labels       string?              Comma-separated label set. When present, the display shows `labels[round(value)]` instead of the numeric value — for stepped enum-like knobs (e.g. "Off,Low,Med,High").
 * @attribute description  string?              Tooltip text — applied as `title="..."` on the ring and label.
 * @attribute disabled     boolean   default=false   Non-interactive.
 *
 * @property  min       number    Live numeric min.
 * @property  max       number    Live numeric max.
 * @property  step      number    Live numeric step (0 disables snapping).
 * @property  value     number    Live numeric value.
 * @property  disabled  boolean   Live disabled state.
 *
 * @event ry:input
 *        detail: { value: number, percent: number }
 *        fires:  on every micro-step during interaction (drag move, wheel tick, keyboard step). `percent` is `(value - min) / (max - min)` ∈ [0, 1].
 *
 * @event ry:beforechange
 *        detail: { value: number, percent: number, previous: number }
 *        fires:  before each commit — at the top of a wheel tick / keyboard step (before the value moves) and on drag release (the value has already moved live during the drag; `previous` is the value at drag start). `ry:input` is the live stream and is NOT intercepted.
 *        cancelable: preventDefault() cancels the commit — wheel/keyboard: the value does not move; drag: the knob snaps back to `previous` (one trailing `ry:input` announces the revert). `ry:change` does not fire either way.
 *
 * @event ry:change
 *        detail: { value: number, percent: number }
 *        fires:  on commit — drag release, wheel tick, keyboard step. For keyboard there is no separate "release" so both `ry:input` and `ry:change` fire per keystroke (mirrors ry-slider and ry-number-select).
 *
 * @keyboard  ArrowUp / ArrowRight — increment by `step`. ArrowDown / ArrowLeft — decrement. PageUp / PageDown — jump 10% of range. Home / End — jump to min / max. Disabled: all suppressed.
 *
 * @state     `data-dragging` present on the host during an active drag.
 *
 * @content   None — `<ry-knob>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-knob[min][max][value]...   aria-valuenow=value  aria-valuemin=min  aria-valuemax=max
 *     > div.ry-knob__ring          [data-ry-target="ring"]   (rotates via --knob-rotation CSS var)
 *       > div.ry-knob__cap         [data-ry-target="cap"]
 *         > div.ry-knob__indicator [data-ry-target="indicator"]
 *     > span.ry-knob__value        [data-ry-target="display"]   {formatted value or label}
 *     > span.ry-knob__label        [data-ry-target="label"]   (only when [label])
 *
 * @gotcha  Drag axis is VERTICAL — moving the cursor UP increases the value (matches real-world physical knob feel). Horizontal drag does nothing. Sensitivity is fixed at `(max - min) / 100` per pixel.
 *
 * @gotcha  `[labels]` replaces the numeric display with the label at `round(value)`. With `labels="Off,Low,Med,High"` and `value=2.4`, display shows `"Med"` (index 2). For a continuous numeric display, omit `[labels]`.
 *
 * @gotcha  Drag interactions can't be exercised in happy-dom (no pointer layout). Vitest covers keyboard / wheel / programmatic; cover drag with Playwright if needed.
 *
 * @gotcha  Keyboard fires BOTH `ry:input` AND `ry:change` on every keystroke. The drag path fires `ry:input` per micro-step and `ry:change` only on release.
 *
 * @gotcha  The visual rotation arc spans 270° (-135° to +135°). The knob does not represent the full 360° — by design, matches a physical potentiometer.
 *
 * @example basic
 * ```html
 * <ry-knob min="0" max="100" value="50" label="Volume"></ry-knob>
 * ```
 *
 * @example with-named-positions
 * ```html
 * <ry-knob min="0" max="3" step="1" value="0" labels="Off,Low,Med,High" label="Fan"></ry-knob>
 * ```
 *
 * @example with-description
 * ```html
 * <ry-knob min="-10" max="10" value="0" step="0.5" label="EQ" description="Bass boost in dB"></ry-knob>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-knob min="0" max="100" value="50" disabled></ry-knob>
 * ```
 *
 * @tutorial mount-renders-ring-and-display
 * ```js
 * const el = mount('<ry-knob min="0" max="100" value="50"></ry-knob>');
 * await expectEqual(!!el.querySelector('[data-ry-target="ring"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="cap"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="indicator"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="display"]'), true);
 * ```
 *
 * @tutorial label-renders-when-set
 * ```js
 * const el = mount('<ry-knob min="0" max="100" value="50" label="Volume"></ry-knob>');
 * await expectEqual(el.querySelector('[data-ry-target="label"]')?.textContent, 'Volume');
 * ```
 *
 * @tutorial no-label-no-slot
 * ```js
 * const el = mount('<ry-knob min="0" max="100" value="50"></ry-knob>');
 * await expectEqual(el.querySelector('[data-ry-target="label"]'), null);
 * ```
 *
 * @tutorial display-shows-numeric-by-default
 * ```js
 * // Display format depends on step magnitude — step>=1 shows integer,
 * // step<1 shows decimals scaled to the value range. Explicit step
 * // pins behavior; without one, ry-knob's formatter falls back to a
 * // magnitude-based decimal scheme (10..99 → "42.0", etc.).
 * const el = mount('<ry-knob min="0" max="100" value="42" step="1"></ry-knob>');
 * await expectEqual(el.querySelector('[data-ry-target="display"]')?.textContent, '42');
 * ```
 *
 * @tutorial labels-attribute-replaces-numeric-display
 * ```js
 * // Wrong assumption: labels just decorate alongside the number. They
 * // REPLACE the numeric display with the corresponding label string.
 * const el = mount('<ry-knob min="0" max="3" step="1" value="2" labels="Off,Low,Med,High"></ry-knob>');
 * await expectEqual(el.querySelector('[data-ry-target="display"]')?.textContent, 'Med');
 * ```
 *
 * @tutorial aria-attributes-on-host
 * ```js
 * const el = mount('<ry-knob min="0" max="200" value="80"></ry-knob>');
 * await expectAttr(el, 'aria-valuenow', '80');
 * await expectAttr(el, 'aria-valuemin', '0');
 * await expectAttr(el, 'aria-valuemax', '200');
 * ```
 *
 * @tutorial keyboard-arrow-increments
 * ```js
 * const el = mount('<ry-knob min="0" max="100" step="5" value="50"></ry-knob>');
 * const fired = { input: [], change: [] };
 * el.addEventListener('ry:input', (e) => fired.input.push(e.detail.value));
 * el.addEventListener('ry:change', (e) => fired.change.push(e.detail.value));
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(el.value, 55);
 * await expectEqual(fired.input[0], 55, 'ry:input fires');
 * await expectEqual(fired.change[0], 55, 'ry:change ALSO fires on keyboard');
 * ```
 *
 * @tutorial pageup-jumps-10-percent
 * ```js
 * const el = mount('<ry-knob min="0" max="200" value="50"></ry-knob>');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'PageUp', bubbles: true }));
 * await expectEqual(el.value, 70, '50 + (200 - 0) / 10 = 70');
 * ```
 *
 * @tutorial home-and-end-jump-to-extremes
 * ```js
 * const el = mount('<ry-knob min="0" max="100" value="42"></ry-knob>');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'End', bubbles: true }));
 * await expectEqual(el.value, 100);
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Home', bubbles: true }));
 * await expectEqual(el.value, 0);
 * ```
 *
 * @tutorial value-clamps-at-extremes
 * ```js
 * const el = mount('<ry-knob min="0" max="10" step="1" value="10"></ry-knob>');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(el.value, 10);
 * ```
 *
 * @tutorial disabled-suppresses-keyboard
 * ```js
 * const el = mount('<ry-knob min="0" max="100" value="50" disabled></ry-knob>');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(el.value, 50);
 * await expectEqual(fired.length, 0);
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange cancels the commit — for a keyboard
 * // step the value never moves and neither ry:input nor ry:change fires.
 * const el = mount('<ry-knob min="0" max="100" step="5" value="50"></ry-knob>');
 * const fired = { input: [], change: [] };
 * el.addEventListener('ry:input', (e) => fired.input.push(e.detail.value));
 * el.addEventListener('ry:change', (e) => fired.change.push(e.detail.value));
 * let seen = null;
 * el.addEventListener('ry:beforechange', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(seen.value, 55, 'detail.value is the NEXT value');
 * await expectEqual(seen.previous, 50, 'detail.previous is the current value');
 * await expectEqual(el.value, 50, 'vetoed step leaves the value at 50');
 * await expectEqual(fired.change.length, 0, 'ry:change did not fire');
 * await expectEqual(fired.input.length, 0, 'ry:input did not fire either');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(el.value, 55, 'without the veto, the step commits');
 * await expectEqual(fired.change[0], 55);
 * ```
 *
 * @see ry-slider
 * @see ry-number-select
 */

import { RyElement } from '../core/ry-element.js';

export class RyKnob extends RyElement {
  #dragging = false;
  #startY = 0;
  #startValue = 0;
  #ring: HTMLElement | null = null;
  #indicator: HTMLElement | null = null;
  #display: HTMLElement | null = null;

  static observedAttributes = ['min', 'max', 'step', 'value', 'disabled', 'label', 'labels', 'description'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#bindEvents();
    this.#updateVisuals();
  }

  #buildMarkup(): void {
    const label = this.getAttribute('label');
    const description = this.getAttribute('description');
    const titleAttr = description ? ` title="${description}"` : '';

    this.innerHTML = `
      <div data-ry-target="ring" class="ry-knob__ring"${titleAttr}>
        <div data-ry-target="cap" class="ry-knob__cap">
          <div data-ry-target="indicator" class="ry-knob__indicator"></div>
        </div>
      </div>
      <span data-ry-target="display" class="ry-knob__value"></span>
      ${label ? `<span data-ry-target="label" class="ry-knob__label"${titleAttr}>${label}</span>` : ''}
    `;

    this.#ring = this.$('[data-ry-target="ring"]');
    this.#indicator = this.$('[data-ry-target="indicator"]');
    this.#display = this.$('[data-ry-target="display"]');
  }

  #bindEvents(): void {
    if (!this.#ring) return;

    // Mouse drag
    this.on(this.#ring, 'mousedown', this.#handleMouseDown);
    this.on(document, 'mousemove', this.#handleMouseMove);
    this.on(document, 'mouseup', this.#handleMouseUp);

    // Touch support
    this.on(this.#ring, 'touchstart', this.#handleTouchStart);
    this.on(document, 'touchmove', this.#handleTouchMove);
    this.on(document, 'touchend', this.#handleTouchEnd);

    // Wheel
    this.on(this.#ring, 'wheel', this.#handleWheel);

    // Keyboard
    this.on(this, 'keydown', this.#handleKeydown);
  }

  #handleMouseDown = (e: MouseEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#dragging = true;
    this.#startY = e.clientY;
    this.#startValue = this.value;
    this.#ring?.classList.add('ry-knob__ring--dragging');
    this.setAttribute('data-dragging', '');
  };

  #handleMouseMove = (e: MouseEvent): void => {
    if (!this.#dragging) return;
    const deltaY = this.#startY - e.clientY;
    const sensitivity = (this.max - this.min) / 100;
    this.#updateValue(this.#startValue + deltaY * sensitivity);
  };

  #handleMouseUp = (): void => {
    if (this.#dragging) {
      this.#dragging = false;
      this.#ring?.classList.remove('ry-knob__ring--dragging');
      this.removeAttribute('data-dragging');
      this.#commitDrag();
    }
  };

  #handleTouchStart = (e: TouchEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    this.#dragging = true;
    this.#startY = e.touches[0]?.clientY ?? 0;
    this.#startValue = this.value;
    this.#ring?.classList.add('ry-knob__ring--dragging');
    this.setAttribute('data-dragging', '');
  };

  #handleTouchMove = (e: TouchEvent): void => {
    if (!this.#dragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    const deltaY = this.#startY - touch.clientY;
    const sensitivity = (this.max - this.min) / 100;
    this.#updateValue(this.#startValue + deltaY * sensitivity);
  };

  #handleTouchEnd = (): void => {
    if (this.#dragging) {
      this.#dragging = false;
      this.#ring?.classList.remove('ry-knob__ring--dragging');
      this.removeAttribute('data-dragging');
      this.#commitDrag();
    }
  };

  #handleWheel = (e: WheelEvent): void => {
    if (this.disabled) return;
    e.preventDefault();
    const step = this.step || (this.max - this.min) / 50;
    const delta = e.deltaY < 0 ? step : -step;
    this.#commit(this.value + delta);
  };

  #handleKeydown = (e: KeyboardEvent): void => {
    if (this.disabled) return;

    const step = this.step || 1;
    const largeStep = (this.max - this.min) / 10;
    let delta = 0;

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        delta = step;
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        delta = -step;
        break;
      case 'PageUp':
        delta = largeStep;
        break;
      case 'PageDown':
        delta = -largeStep;
        break;
      case 'Home':
        this.#commit(this.min);
        return;
      case 'End':
        this.#commit(this.max);
        return;
      default:
        return;
    }

    e.preventDefault();
    this.#commit(this.value + delta);
  };

  /** Clamp to [min, max] and snap to `step` (when > 0). */
  #normalize(newValue: number): number {
    let clamped = Math.max(this.min, Math.min(this.max, newValue));
    const step = this.step;
    if (step > 0) {
      clamped = Math.round((clamped - this.min) / step) * step + this.min;
      clamped = Math.max(this.min, Math.min(this.max, clamped));
    }
    return clamped;
  }

  /**
   * Instant commit (wheel, keyboard): the interception point fires BEFORE the
   * value moves, so a veto means no ry:input and no ry:change.
   */
  #commit(newValue: number): void {
    const next = this.#normalize(newValue);
    if (!this.#emitBeforeChange(next, this.value)) return;
    this.#updateValue(next);
    this.#emitChange();
  }

  /**
   * Drag commit (mouse/touch release): the value already moved live via
   * ry:input; `#startValue` (captured at drag start) is `previous`, and a
   * veto snaps back to it.
   */
  #commitDrag(): void {
    if (!this.#emitBeforeChange(this.value, this.#startValue)) {
      this.#updateValue(this.#startValue);
      return;
    }
    this.#emitChange();
  }

  #updateValue(newValue: number): void {
    this.value = this.#normalize(newValue);
    this.#updateVisuals();
    this.#emitInput();
  }

  #updateVisuals(): void {
    const percent = (this.value - this.min) / (this.max - this.min);
    // Rotation: -135deg (min) to +135deg (max) = 270deg range
    const rotation = -135 + percent * 270;

    if (this.#ring) {
      this.#ring.style.setProperty('--knob-rotation', `${rotation}deg`);
      this.#ring.style.setProperty('--knob-percent', String(percent));
    }

    if (this.#display) {
      this.#display.textContent = this.#formatValue(this.value);
    }

    // Update ARIA
    this.setAttribute('aria-valuenow', String(this.value));
    this.setAttribute('aria-valuemin', String(this.min));
    this.setAttribute('aria-valuemax', String(this.max));
  }

  #formatValue(value: number): string {
    const labelsAttr = this.getAttribute('labels');
    if (labelsAttr) {
      const labels = labelsAttr.split(',');
      const index = Math.round(value);
      return labels[index] ?? String(index);
    }

    const step = this.step;
    if (step >= 1) return Math.round(value).toString();
    if (Math.abs(value) >= 1000) return (value / 1000).toFixed(1) + 'k';
    if (Math.abs(value) >= 100) return Math.round(value).toString();
    if (Math.abs(value) >= 10) return value.toFixed(1);
    return value.toFixed(2);
  }

  #emitInput(): void {
    const percent = (this.value - this.min) / (this.max - this.min);
    this.emit('input', { value: this.value, percent });
  }

  #emitChange(): void {
    const percent = (this.value - this.min) / (this.max - this.min);
    this.emit('change', { value: this.value, percent });
  }

  /** `ry:beforechange` — the after-event's shape plus `previous`. */
  #emitBeforeChange(next: number, previous: number): boolean {
    const percent = (next - this.min) / (this.max - this.min);
    return this.emit('beforechange', { value: next, percent, previous });
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (name === 'value' && this.#ring) {
      this.#updateVisuals();
    }
  }

  // --- Public API ---

  get min(): number {
    return parseFloat(this.getAttribute('min') ?? '0');
  }
  set min(val: number) {
    this.setAttribute('min', String(val));
  }

  get max(): number {
    return parseFloat(this.getAttribute('max') ?? '100');
  }
  set max(val: number) {
    this.setAttribute('max', String(val));
  }

  get step(): number {
    return parseFloat(this.getAttribute('step') ?? '0');
  }
  set step(val: number) {
    this.setAttribute('step', String(val));
  }

  get value(): number {
    return parseFloat(this.getAttribute('value') ?? String(this.min));
  }
  set value(val: number) {
    this.setAttribute('value', String(val));
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }
  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }
}

customElements.define('ry-knob', RyKnob);
